import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_CUSTOMER_ESIMS_QUERY,
  SINGLE_CUSTOMER_ESIM_QUERY,
  CREATE_CUSTOMER_ESIM_MUTATION,
  UPDATE_CUSTOMER_ESIM_MUTATION,
  DELETE_CUSTOMER_ESIM_MUTATION,
  FORCE_DELETE_CUSTOMER_ESIM_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const customerEsimRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_CUSTOMER_ESIM_QUERY, variables).then((data: any) => {
      return hydrateObject(data.customerEsim)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_CUSTOMER_ESIMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.customerEsims)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_CUSTOMER_ESIM_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.customerEsim),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_CUSTOMER_ESIMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.customerEsims),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_CUSTOMER_ESIM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.customerEsim)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_CUSTOMER_ESIM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.customerEsim)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_CUSTOMER_ESIM_MUTATION, variables).then((data: any) => {
      return data.deleteCustomerEsim
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_CUSTOMER_ESIM_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCustomerEsim
    })
  },
}

export default customerEsimRepository
